import { i18n } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { Shell, timeAgo } from '@sdk';
import { Button } from '@src/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@src/components/ui/popover';
import { Switch } from '@src/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@src/components/ui/tooltip';
import { notify } from '@src/notifications';
import { Plural, Trans } from '@lingui/react/macro';
import {
  ERROR_TIME_SPANS,
  ErrorCategory,
  ErrorStatus,
  useClaudeErrorRecords,
  type ClaudeErrorRecord,
  type CloudSearchResult,
  type ErrorOccurrence,
} from '@src/hooks/useClaudeErrorRecords';
import { useContext } from '@src/hooks/useContext';
import { CloudSearchResultsModal } from './CloudSearchResultsModal';
import { cn } from '@src/lib/utils';
import { DockPointer } from '@src/navigation/DockPointer';
import { useDockNavigation } from '@src/navigation/useDockNavigation';
import { ViewType } from '@src/types/ViewType';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@src/components/ui/alert-dialog';

import {
  AlarmClock,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  EyeOff,
  FileText,
  ListTodo,
  Loader2,
  OctagonAlert,
  RefreshCw,
  RotateCcw,
  Search,
  Terminal,
  Trash2,
  Webhook,
  Wrench,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  STATUS_SLUG_MAP,
  UNKNOWN_TIMESTAMP,
  parseTranscriptPath,
  slugFromStatusFilter,
  statusFilterFromSlug,
} from './error-viewer-utils';

/** Lazy descriptors: a `t` macro in this module-level table would resolve once
 *  at import, before a catalog is active, and freeze English into the menu. The
 *  render site already resolves with `i18n._`. */
const SNOOZE_OPTIONS = [
  { label: msg`1 hour`, ms: 3_600_000 },
  { label: msg`4 hours`, ms: 14_400_000 },
  { label: msg`24 hours`, ms: 86_400_000 },
  { label: msg`1 week`, ms: 604_800_000 },
] as const;

function statusBadge(status: ErrorStatus) {
  switch (status) {
    case ErrorStatus.OPEN:
      return null; // no badge for open
    case ErrorStatus.IGNORED:
      return (
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
          <EyeOff className="me-0.5 inline h-2.5 w-2.5" />
          <Trans>Ignored</Trans>
        </span>
      );
    case ErrorStatus.IGNORED_UNTIL:
      return (
        <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-600">
          <Clock className="me-0.5 inline h-2.5 w-2.5" />
          <Trans>Snoozed</Trans>
        </span>
      );
    case ErrorStatus.TASK_CREATED:
      return (
        <span className="rounded bg-blue-500/10 px-1.5 py-0.5 text-[10px] text-blue-600">
          <ListTodo className="me-0.5 inline h-2.5 w-2.5" />
          <Trans>Tasked</Trans>
        </span>
      );
    default:
      return null;
  }
}

// ─── Session link button ────────────────────────────────────────────────────

function SessionLink({
  jsonlPath,
  timestamp,
  onOpenTranscript,
}: {
  jsonlPath: string;
  timestamp?: string;
  onOpenTranscript: (jsonlPath: string, timestamp?: string) => void;
}) {
  const hasTranscript = !!jsonlPath;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          className={cn(
            'rounded p-0.5 transition-colors',
            hasTranscript
              ? 'text-muted-foreground hover:bg-muted hover:text-foreground'
              : 'cursor-default text-muted-foreground/30',
          )}
          disabled={!hasTranscript}
          onClick={() => onOpenTranscript(jsonlPath, timestamp)}
        >
          <FileText className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {hasTranscript ? <Trans>View session transcript</Trans> : <Trans>Transcript not found</Trans>}
      </TooltipContent>
    </Tooltip>
  );
}

// ─── Action buttons ──────────────────────────────────────────────────────────

const actionBtnClass = 'rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground';

function ErrorActions({
  error,
  onIgnore,
  onIgnoreTillNow,
  onSnooze,
  onReopen,
}: {
  error: ClaudeErrorRecord;
  onIgnore: (fp: string) => void;
  onIgnoreTillNow: (fp: string) => void;
  onSnooze: (fp: string, until: Date) => void;
  onReopen: (fp: string) => void;
}) {
  const fp = error.fingerprint;
  const isOpen = error.error_status === ErrorStatus.OPEN;

  return (
    <>
      {isOpen && (
        <>
          <Tooltip>
            <TooltipTrigger asChild>
              <button className={actionBtnClass} onClick={() => onIgnoreTillNow(fp)}>
                <Clock className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <Trans>Ignore till now</Trans>
            </TooltipContent>
          </Tooltip>
          <Popover>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <button className={actionBtnClass}>
                    <AlarmClock className="h-3.5 w-3.5" />
                  </button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <Trans>Snooze</Trans>
              </TooltipContent>
            </Tooltip>
            <PopoverContent side="bottom" align="end" className="w-28 p-1">
              {SNOOZE_OPTIONS.map((opt) => (
                <button
                  key={opt.ms}
                  className="w-full rounded px-2 py-1 text-start text-xs hover:bg-muted"
                  onClick={() => onSnooze(fp, new Date(Date.now() + opt.ms))}
                >
                  {i18n._(opt.label)}
                </button>
              ))}
            </PopoverContent>
          </Popover>
          <Tooltip>
            <TooltipTrigger asChild>
              <button className={actionBtnClass} onClick={() => onIgnore(fp)}>
                <EyeOff className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <Trans>Ignore permanently</Trans>
            </TooltipContent>
          </Tooltip>
        </>
      )}
      {!isOpen && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button className={actionBtnClass} onClick={() => onReopen(fp)}>
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <Trans>Reopen</Trans>
          </TooltipContent>
        </Tooltip>
      )}
    </>
  );
}

// ─── Error card (deduplicated view) ─────────────────────────────────────────

function ErrorCard({
  error,
  grouped,
  onOpenTranscript,
  onOpenHook,
  onIgnore,
  onIgnoreTillNow,
  onSnooze,
  onReopen,
  onCreateTask,
  onGoToSession,
}: {
  error: ClaudeErrorRecord;
  grouped?: boolean;
  onOpenTranscript: (jsonlPath: string, timestamp?: string) => void;
  onOpenHook: (hookName: string, eventType: string) => void;
  onIgnore: (fp: string) => void;
  onIgnoreTillNow: (fp: string) => void;
  onSnooze: (fp: string, until: Date) => void;
  onReopen: (fp: string) => void;
  onCreateTask: (error: ClaudeErrorRecord) => void;
  onGoToSession: (error: ClaudeErrorRecord) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const isHook = error.error_category === ErrorCategory.HOOK;
  const occurrences = error.occurrence_count;
  const Icon = isHook ? AlertTriangle : OctagonAlert;
  const iconColor = isHook ? 'text-amber-400' : 'text-orange-400';
  const needsTruncation =
    error.error_msg.length > 120 ||
    (error.fix.message && error.fix.message.length > 120) ||
    (error.fix.instruction && error.fix.instruction.length > 120) ||
    (error.traceback && error.traceback.length > 0);
  const trim = (s: string) => (!expanded && s.length > 120 ? s.slice(0, 120) + '...' : s);

  const handleCardClick = useCallback(
    (e: React.MouseEvent) => {
      // Don't toggle if clicking a button, link, or interactive element
      if ((e.target as HTMLElement).closest('button, a, [role="button"]')) return;
      if (needsTruncation) setExpanded((v) => !v);
    },
    [needsTruncation],
  );

  return (
    <div
      className={cn(
        'rounded-md border border-border bg-card p-3',
        'border-s-2',
        isHook ? 'border-s-amber-500' : 'border-s-orange-500',
        needsTruncation && 'cursor-pointer',
      )}
      onClick={handleCardClick}
    >
      <div className="flex gap-2">
        {/* Left gutter: icon + expand/collapse chevron */}
        <div className="flex shrink-0 flex-col items-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Icon className={cn('h-4 w-4 cursor-default', iconColor)} />
            </TooltipTrigger>
            <TooltipContent>
              <Trans>fingerprint: {error.fingerprint}</Trans>
            </TooltipContent>
          </Tooltip>
          {needsTruncation && (
            <span className="text-muted-foreground/50">
              {expanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 rtl:-scale-x-100" />
              )}
            </span>
          )}
        </div>
        {/* Occurrence counter in grouped mode */}
        {grouped && error.occurrence_count > 1 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-muted px-1 text-[10px] font-bold text-muted-foreground">
                {error.occurrence_count}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <Plural
                value={error.session_ids.length}
                one={`${occurrences} occurrences across # session`}
                other={`${occurrences} occurrences across # sessions`}
              />
            </TooltipContent>
          </Tooltip>
        )}

        {/* Left: text content */}
        <div className="min-w-0 flex-1">
          {/* Header row: hook name + event badge + status (hooks only) */}
          {isHook && (
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-xs font-medium">{error.hook}</span>
              {error.event && (
                <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{error.event}</span>
              )}
              {statusBadge(error.error_status)}
            </div>
          )}
          {/* Status badge for non-hook errors */}
          {!isHook && statusBadge(error.error_status) && (
            <div className="flex items-center gap-1">{statusBadge(error.error_status)}</div>
          )}

          {/* Error message (root cause) */}
          <p
            className={cn(
              'font-mono text-xs',
              isHook ? 'text-amber-400/90' : 'text-foreground/75',
              (isHook || statusBadge(error.error_status)) && 'mt-0.5',
            )}
          >
            {trim(error.error_msg)}
          </p>

          {/* Occurrence info — compact single line */}
          <p className="text-[10px] text-muted-foreground">
            <Plural
              value={error.session_ids.length}
              one={`${occurrences}× across # session`}
              other={`${occurrences}× across # sessions`}
            />
            {error.last_seen && error.last_seen !== UNKNOWN_TIMESTAMP && (
              <span className="text-muted-foreground/60"> · {timeAgo(new Date(error.last_seen))}</span>
            )}
          </p>

          {/* Expanded-only details */}
          {expanded && (
            <>
              {/* Hook events where this error occurs */}
              {isHook &&
                (() => {
                  const display: string[] = error.hooks?.length > 0 ? error.hooks : error.hook ? [error.hook] : [];
                  return display.length > 0 ? (
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      <span className="text-[10px] text-muted-foreground">
                        <Trans>Hooks:</Trans>
                      </span>
                      {display.map((h) => (
                        <span key={h} className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                          {h}
                        </span>
                      ))}
                    </div>
                  ) : null;
                })()}

              {/* Fix message & instruction — full width, readable */}
              {error.error_status === ErrorStatus.OPEN && (error.fix.message || error.fix.instruction) && (
                <div className="mt-1.5 rounded-md border border-blue-500/20 bg-blue-500/5 px-2.5 py-1.5">
                  {error.fix.message && <p className="text-xs font-medium text-blue-400">{error.fix.message}</p>}
                  {error.fix.instruction && (
                    <p className="mt-0.5 text-xs text-muted-foreground">{error.fix.instruction}</p>
                  )}
                </div>
              )}

              {/* Traceback */}
              {error.traceback && error.traceback.length > 0 && (
                <>
                  <p className="mt-1.5 text-[10px] text-muted-foreground">
                    <Trans>Traceback ({error.traceback.length} lines)</Trans>
                  </p>
                  <pre className="mt-1 max-h-48 overflow-auto rounded bg-muted p-2 text-[10px] leading-relaxed text-muted-foreground">
                    {error.traceback.join('\n')}
                  </pre>
                </>
              )}
            </>
          )}

          {/* Collapsed: show trimmed fix hint if available */}
          {!expanded && error.error_status === ErrorStatus.OPEN && (error.fix.message || error.fix.instruction) && (
            <p className="text-[10px] text-blue-400/70">{trim(error.fix.message || error.fix.instruction || '')}</p>
          )}
        </div>

        {/* Right: buttons */}
        <div className="flex shrink-0 flex-col items-end gap-1">
          <div className="flex items-center gap-1">
            <SessionLink
              jsonlPath={error.last_jsonl_path}
              timestamp={error.last_seen}
              onOpenTranscript={onOpenTranscript}
            />
            {isHook && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className={actionBtnClass} onClick={() => onOpenHook(error.hook, error.event)}>
                    <Webhook className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <Trans>View hook config</Trans>
                </TooltipContent>
              </Tooltip>
            )}
            {error.error_status === ErrorStatus.TASK_CREATED && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className={actionBtnClass} onClick={() => onGoToSession(error)}>
                    <Terminal className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <Trans>Go to session</Trans>
                </TooltipContent>
              </Tooltip>
            )}
            <ErrorActions
              error={error}
              onIgnore={onIgnore}
              onIgnoreTillNow={onIgnoreTillNow}
              onSnooze={onSnooze}
              onReopen={onReopen}
            />
          </div>
          {error.error_status === ErrorStatus.OPEN && (
            <Button
              variant="default"
              size="sm"
              className="h-8 w-24 gap-1.5 border-0 bg-green-600 text-sm font-semibold text-white shadow-sm hover:bg-green-500 active:bg-green-700"
              onClick={() => onCreateTask(error)}
            >
              <Wrench className="h-3.5 w-3.5" />
              <Trans>Fix It</Trans>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Occurrence card (expanded/individual view) ─────────────────────────────

function OccurrenceCard({
  occurrence,
  parentError,
  onOpenTranscript,
  onOpenHook,
  onIgnore,
  onIgnoreTillNow,
  onSnooze,
  onReopen,
  onCreateTask,
  onGoToSession,
}: {
  occurrence: ErrorOccurrence;
  parentError: ClaudeErrorRecord;
  onOpenTranscript: (jsonlPath: string, timestamp?: string) => void;
  onOpenHook: (hookName: string, eventType: string) => void;
  onIgnore: (fp: string) => void;
  onIgnoreTillNow: (fp: string) => void;
  onSnooze: (fp: string, until: Date) => void;
  onReopen: (fp: string) => void;
  onCreateTask: (error: ClaudeErrorRecord) => void;
  onGoToSession: (error: ClaudeErrorRecord) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const isHook = parentError.error_category === ErrorCategory.HOOK;
  const Icon = isHook ? AlertTriangle : OctagonAlert;
  const iconColor = isHook ? 'text-amber-400' : 'text-orange-400';
  const needsTruncation =
    occurrence.error_msg.length > 120 || (occurrence.traceback && occurrence.traceback.length > 0);
  const trim = (s: string) => (!expanded && s.length > 120 ? s.slice(0, 120) + '...' : s);

  const handleCardClick = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('button, a, [role="button"]')) return;
      if (needsTruncation) setExpanded((v) => !v);
    },
    [needsTruncation],
  );

  return (
    <div
      className={cn(
        'rounded-md border border-border bg-card p-2.5',
        'border-s-2',
        isHook ? 'border-s-amber-500' : 'border-s-orange-500',
        needsTruncation && 'cursor-pointer',
      )}
      onClick={handleCardClick}
    >
      <div className="flex gap-2">
        {/* Left gutter: icon + expand/collapse chevron */}
        <div className="flex shrink-0 flex-col items-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Icon className={cn('h-3.5 w-3.5 cursor-default', iconColor)} />
            </TooltipTrigger>
            <TooltipContent>
              <Trans>fingerprint: {parentError.fingerprint}</Trans>
            </TooltipContent>
          </Tooltip>
          {needsTruncation && (
            <span className="text-muted-foreground/50">
              {expanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 rtl:-scale-x-100" />
              )}
            </span>
          )}
        </div>

        {/* Left: text content */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1">
            {statusBadge(parentError.error_status)}
            <span className="text-[10px] text-muted-foreground/60">{occurrence.session_id.slice(0, 12)}...</span>
          </div>

          {/* Error message (root cause) first */}
          <p className={cn('mt-0.5 font-mono text-xs', isHook ? 'text-amber-400/90' : 'text-foreground/75')}>
            {trim(occurrence.error_msg)}
          </p>

          {/* Timestamp — compact */}
          {occurrence.timestamp && occurrence.timestamp !== UNKNOWN_TIMESTAMP && (
            <p className="text-[10px] text-muted-foreground/60">{timeAgo(new Date(occurrence.timestamp))}</p>
          )}

          {/* Expanded-only details */}
          {expanded && (
            <>
              {/* Hook where this occurrence happened */}
              {isHook && (occurrence.hook || parentError.hook) && (
                <div className="mt-1 flex flex-wrap items-center gap-1">
                  <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                    {occurrence.hook || parentError.hook}
                  </span>
                </div>
              )}
            </>
          )}

          {/* Traceback — only visible when expanded */}
          {expanded && occurrence.traceback && occurrence.traceback.length > 0 && (
            <>
              <p className="mt-1.5 text-[10px] text-muted-foreground">
                <Trans>Traceback ({occurrence.traceback.length} lines)</Trans>
              </p>
              <pre className="mt-1 max-h-48 overflow-auto rounded bg-muted p-2 text-[10px] leading-relaxed text-muted-foreground">
                {occurrence.traceback.join('\n')}
              </pre>
            </>
          )}
        </div>

        {/* Right: buttons */}
        <div className="flex shrink-0 flex-col items-end gap-1">
          <div className="flex items-center gap-1">
            <SessionLink
              jsonlPath={occurrence.jsonl_path}
              timestamp={occurrence.timestamp}
              onOpenTranscript={onOpenTranscript}
            />
            {isHook && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className={actionBtnClass} onClick={() => onOpenHook(parentError.hook, parentError.event)}>
                    <Webhook className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <Trans>View hook config</Trans>
                </TooltipContent>
              </Tooltip>
            )}
            {parentError.error_status === ErrorStatus.TASK_CREATED && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className={actionBtnClass} onClick={() => onGoToSession(parentError)}>
                    <Terminal className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <Trans>Go to session</Trans>
                </TooltipContent>
              </Tooltip>
            )}
            <ErrorActions
              error={parentError}
              onIgnore={onIgnore}
              onIgnoreTillNow={onIgnoreTillNow}
              onSnooze={onSnooze}
              onReopen={onReopen}
            />
          </div>
          {parentError.error_status === ErrorStatus.OPEN && (
            <Button
              variant="default"
              size="sm"
              className="h-8 w-24 gap-1.5 border-0 bg-green-600 text-sm font-semibold text-white shadow-sm hover:bg-green-500 active:bg-green-700"
              onClick={() => onCreateTask(parentError)}
            >
              <Wrench className="h-3.5 w-3.5" />
              <Trans>Fix It</Trans>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main viewer ────────────────────────────────────────────────────────────

interface ClaudeErrorsViewerProps {
  initialStatusSlug?: string;
}

export function ClaudeErrorsViewer({ initialStatusSlug }: ClaudeErrorsViewerProps) {
  const {
    allErrors,
    filteredErrors,
    displayCount,
    openDisplayCount,
    statusCounts,
    timeSpan,
    setTimeSpan,
    statusFilter,
    setStatusFilter,
    deduplicate,
    setDeduplicate,
    spanMs,
    isLoading,
    refetch,
    ignoreAll,
    ignoreTillNow,
    ignoreUntil,
    reopenError,
    createTaskForError,
    clearAll,
    searchCloudForErrors,
    fixAllCloud,
  } = useClaudeErrorRecords();

  const { cloudLoginAvailable } = useContext();
  const { navigation } = useDockNavigation();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  // Cloud search state
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isSearchingCloud, setIsSearchingCloud] = useState(false);
  const [cloudResultsModal, setCloudResultsModal] = useState<{
    ignored: number;
    fixResults: CloudSearchResult[];
    remaining: number;
  } | null>(null);

  // Apply initial status slug from URL on mount
  useMemo(() => {
    if (initialStatusSlug) {
      const resolved = statusFilterFromSlug(initialStatusSlug);
      if (resolved !== statusFilter) setStatusFilter(resolved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStatusFilter = useCallback(
    (value: ErrorStatus | 'all') => {
      setStatusFilter(value);
      navigation.openLens('heartbeat', 'errors', slugFromStatusFilter(value));
    },
    [navigation, setStatusFilter],
  );

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    void refetch().finally(() => setIsRefreshing(false));
  }, [refetch]);

  const handleClearAll = useCallback(async () => {
    setIsClearing(true);
    try {
      const result = await clearAll();
      if (!result) {
        notify.success({ title: t`Cleared` });
        return;
      }
      const parts: string[] = [];
      const totalDeleted = result.deleted_debug_logs + result.truncated_debug_logs;
      if (totalDeleted > 0) parts.push(`${totalDeleted} debug log${totalDeleted !== 1 ? 's' : ''}`);
      if (result.deleted_error_records > 0)
        parts.push(`${result.deleted_error_records} error record${result.deleted_error_records !== 1 ? 's' : ''}`);
      const msg = parts.length > 0 ? `Cleared ${parts.join(' and ')}` : 'Nothing to clear';
      const skipped = result.skipped_debug_logs.length;
      notify.success({
        title: msg,
        message:
          result.truncated_debug_logs > 0
            ? `${result.truncated_debug_logs} log${result.truncated_debug_logs !== 1 ? 's' : ''} from active sessions were emptied but not deleted. They will be removed after those sessions end.`
            : skipped > 0
              ? `${skipped} file${skipped !== 1 ? 's' : ''} could not be cleared. Retry after active sessions end.`
              : undefined,
      });
    } catch (e) {
      notify.error({ title: t`Failed to clear errors`, message: String(e) });
    } finally {
      setIsClearing(false);
    }
  }, [clearAll]);

  const handleOpenTranscript = useCallback(
    (jsonlPath: string, timestamp?: string) => {
      if (!jsonlPath) return;
      const parsed = parseTranscriptPath(jsonlPath);
      if (!parsed) return;
      const options: Record<string, string> = {};
      if (timestamp) options.ts = timestamp;
      navigation.openLens('claude', 'transcript', parsed.sessionId, options);
    },
    [navigation],
  );

  const handleOpenHook = useCallback(
    (_hookName: string, eventType: string) => {
      navigation.openDock(
        new DockPointer(ViewType.HOOKS, undefined, {
          hookId: '',
          eventType: eventType || '',
        }),
      );
    },
    [navigation],
  );

  const handleCreateTask = useCallback(
    (error: ClaudeErrorRecord) => {
      const instruction = error.fix?.instruction;
      void createTaskForError(error, instruction ? { instruction } : undefined).then(({ taskId, shellId }) => {
        if (taskId && shellId) {
          const description = instruction ? 'Claude is applying the fix.' : 'Claude is investigating the error.';
          notify.success({ title: t`Session started`, message: description });
          void navigation.openSession(shellId, { skipPermissions: true });
        } else {
          notify.error({ title: t`Failed to start session` });
        }
      });
    },
    [createTaskForError, navigation],
  );

  const handleGoToSession = useCallback(
    (error: ClaudeErrorRecord) => {
      if (!error.worker_session_id) return;
      void (async () => {
        let shell: Shell | null = null;
        try {
          shell = await Shell.getById<Shell>(error.worker_session_id);
        } catch {
          /* not found */
        }
        if ((shell as { pty?: { isLive?: boolean } } | null)?.pty?.isLive) {
          void navigation.openSession(error.worker_session_id, { skipPermissions: true });
        } else if (error.claude_session_id) {
          void navigation.openShell(error.worker_session_id, {
            startCommand: `claude --resume ${error.claude_session_id} --dangerously-skip-permissions`,
            skipPermissions: true,
          });
        } else {
          void navigation.openSession(error.worker_session_id);
        }
      })();
    },
    [navigation],
  );

  // Wrap async triage actions as void callbacks for event handlers
  const handleIgnore = useCallback((fp: string) => void ignoreAll(fp), [ignoreAll]);
  const handleIgnoreTillNow = useCallback((fp: string) => void ignoreTillNow(fp), [ignoreTillNow]);
  const handleSnooze = useCallback((fp: string, until: Date) => void ignoreUntil(fp, until), [ignoreUntil]);
  const handleReopen = useCallback((fp: string) => void reopenError(fp), [reopenError]);

  const handleSearchCloud = useCallback(async () => {
    if (!cloudLoginAvailable) {
      setShowLoginModal(true);
      return;
    }
    const openFingerprints = allErrors.filter((e) => e.error_status === ErrorStatus.OPEN).map((e) => e.fingerprint);
    if (openFingerprints.length === 0) return;
    setIsSearchingCloud(true);
    setCloudResultsModal(null);
    try {
      const results = await searchCloudForErrors(openFingerprints);
      const toIgnore = results.filter((r) => r.action === 'ignore');
      const toFix = results.filter((r) => r.action === 'fix');
      const toAnalyse = results.filter((r) => r.action === 'analyse');
      setCloudResultsModal({ ignored: toIgnore.length, fixResults: toFix, remaining: toAnalyse.length });
    } catch (e) {
      notify.error({ title: t`Cloud search failed`, message: String(e) });
    } finally {
      setIsSearchingCloud(false);
    }
  }, [cloudLoginAvailable, allErrors, searchCloudForErrors]);

  // Auto-trigger cloud search on mount (e.g. navigating to this URL)
  const autoSearched = useRef(false);
  useEffect(() => {
    if (autoSearched.current) return;
    if (isLoading || isSearchingCloud) return;
    const hasOpen = allErrors.some((e) => e.error_status === ErrorStatus.OPEN);
    if (!hasOpen) return;
    autoSearched.current = true;
    void handleSearchCloud();
  }, [isLoading, isSearchingCloud, allErrors, handleSearchCloud]);

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex flex-col gap-2 border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <h2 className="text-sm font-medium">
              <Trans>Session Errors</Trans>
            </h2>
            {openDisplayCount > 0 && (
              <>
                <span className="rounded-full bg-destructive px-2 py-0.5 text-[10px] font-bold text-destructive-foreground">
                  {openDisplayCount}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {deduplicate ? (
                    <Plural
                      value={openDisplayCount}
                      one="grouped open error in the last 24 hours"
                      other="grouped open errors in the last 24 hours"
                    />
                  ) : (
                    <Plural
                      value={openDisplayCount}
                      one="open error in the last 24 hours"
                      other="open errors in the last 24 hours"
                    />
                  )}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Time span filter */}
            <div className="flex shrink-0 items-center rounded-md border bg-muted/50">
              {ERROR_TIME_SPANS.map((span, i) => (
                <Tooltip key={span.value}>
                  <TooltipTrigger asChild>
                    <button
                      className={cn(
                        'px-2 py-0.5 text-[10px] font-medium transition-colors',
                        timeSpan === span.value
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground',
                        i === 0 && 'rounded-s-[5px]',
                        i === ERROR_TIME_SPANS.length - 1 && 'rounded-e-[5px]',
                      )}
                      onClick={() => setTimeSpan(span.value)}
                    >
                      {i18n._(span.label)}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{span.tooltip}</TooltipContent>
                </Tooltip>
              ))}
            </div>
            {(statusCounts[ErrorStatus.OPEN] ?? 0) > 0 && (
              <Button
                size="sm"
                className="h-8 gap-1.5 bg-blue-600 px-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 active:bg-blue-700"
                disabled={isSearchingCloud}
                onClick={() => void handleSearchCloud()}
              >
                {isSearchingCloud ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Search className="h-3.5 w-3.5" />
                )}
                <Trans>Search Flowpad</Trans>
              </Button>
            )}
            <Button variant="ghost" size="sm" className="h-7 gap-1.5" onClick={handleRefresh} disabled={isRefreshing}>
              <RefreshCw className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')} />
              <Trans>Refresh</Trans>
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 gap-1.5" disabled={isClearing}>
                  <Trash2 className={cn('h-3.5 w-3.5 -translate-y-px', isClearing && 'animate-pulse')} />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    <Trans>Clear all debug data?</Trans>
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    <Trans>
                      This will delete all debug logs from{' '}
                      <code className="rounded bg-muted px-1 text-xs">~/.claude/debug/</code> and all parsed error
                      records.
                    </Trans>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>
                    <Trans>Cancel</Trans>
                  </AlertDialogCancel>
                  <AlertDialogAction onClick={() => void handleClearAll()}>
                    <Trans>Clear All</Trans>
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Second row: status filter + deduplicate toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center rounded-md border bg-muted/50">
            {STATUS_SLUG_MAP.map((sf, i) => {
              const count = statusCounts[sf.value] ?? 0;
              return (
                <button
                  key={sf.value}
                  className={cn(
                    'flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium transition-colors',
                    statusFilter === sf.value
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                    i === 0 && 'rounded-s-[5px]',
                    i === STATUS_SLUG_MAP.length - 1 && 'rounded-e-[5px]',
                  )}
                  onClick={() => handleStatusFilter(sf.value)}
                >
                  {sf.label}
                  {(sf.value === 'all' || count > 0) && (
                    <span
                      className={cn(
                        'inline-flex h-3.5 min-w-[14px] items-center justify-center rounded-full px-1 text-[9px] font-bold',
                        statusFilter === sf.value
                          ? 'bg-primary-foreground/20 text-primary-foreground'
                          : 'bg-muted-foreground/15 text-muted-foreground',
                      )}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <label className="flex items-center gap-2">
            <Switch checked={deduplicate} onCheckedChange={setDeduplicate} />
            <span className="text-xs font-medium text-muted-foreground">
              <Trans>Group Events</Trans>
            </span>
          </label>
        </div>
      </div>

      {/* Login required modal for cloud search */}
      <AlertDialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Login required</Trans>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>Login is required to search Flowpad database</Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <Trans>Cancel</Trans>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowLoginModal(false);
                void (async () => {
                  const { oauthService, OAUTH_PROVIDERS } = await import('@sdk/services/oauth/oauth-service');
                  await oauthService.connect(OAUTH_PROVIDERS.FLOWPAD_CLOUD);
                })();
              }}
            >
              <Trans>Login</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cloud search results modal */}
      {cloudResultsModal && (
        <CloudSearchResultsModal
          open={!!cloudResultsModal}
          onClose={() => setCloudResultsModal(null)}
          ignored={cloudResultsModal.ignored}
          fixResults={cloudResultsModal.fixResults}
          fixErrors={allErrors.filter((e) => cloudResultsModal.fixResults.some((r) => r.fingerprint === e.fingerprint))}
          remaining={cloudResultsModal.remaining}
          onFixAll={async (fps) => {
            await fixAllCloud(fps);
          }}
        />
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading && (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            <Trans>Loading errors...</Trans>
          </div>
        )}

        {!isLoading &&
          displayCount === 0 &&
          (() => {
            // Find the next wider time span that has errors matching the status filter
            const currentSpanIdx = ERROR_TIME_SPANS.findIndex((t) => t.value === timeSpan);
            let widerSpan: (typeof ERROR_TIME_SPANS)[number] | null = null;
            let widerCount = 0;
            for (let i = currentSpanIdx + 1; i < ERROR_TIME_SPANS.length; i++) {
              const span = ERROR_TIME_SPANS[i];
              const cutoff = Date.now() - span.ms;
              const count = allErrors.filter((e) => {
                const ts = e.last_seen ? new Date(e.last_seen).getTime() : 0;
                if (ts < cutoff) return false;
                if (statusFilter !== 'all' && e.error_status !== statusFilter) return false;
                return true;
              }).length;
              if (count > 0) {
                widerSpan = span;
                widerCount = count;
                break;
              }
            }
            const widerSpanLabel = widerSpan ? i18n._(widerSpan.label) : '';
            return (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <CheckCircle2 className="mb-2 h-8 w-8 opacity-30" />
                <p className="text-sm font-medium">
                  <Trans>No errors found</Trans>
                </p>
                <p className="mt-1 text-xs">
                  {statusFilter === ErrorStatus.OPEN && !widerSpan
                    ? t`All errors have been triaged.`
                    : t`No errors match the current filters.`}
                </p>
                {widerSpan && (
                  <button
                    className="mt-3 rounded bg-muted px-3 py-1 text-xs font-medium transition-colors hover:bg-muted/80"
                    onClick={() => setTimeSpan(widerSpan.value)}
                  >
                    <Plural
                      value={widerCount}
                      one={`# error in the last ${widerSpanLabel} — switch`}
                      other={`# errors in the last ${widerSpanLabel} — switch`}
                    />
                  </button>
                )}
              </div>
            );
          })()}

        {!isLoading && displayCount > 0 && deduplicate && (
          <div className="space-y-2">
            {filteredErrors.map((error) => (
              <ErrorCard
                key={error.fingerprint}
                error={error}
                grouped
                onOpenTranscript={handleOpenTranscript}
                onOpenHook={handleOpenHook}
                onIgnore={handleIgnore}
                onIgnoreTillNow={handleIgnoreTillNow}
                onSnooze={handleSnooze}
                onReopen={handleReopen}
                onCreateTask={handleCreateTask}
                onGoToSession={handleGoToSession}
              />
            ))}
          </div>
        )}

        {!isLoading && displayCount > 0 && !deduplicate && (
          <div className="space-y-2">
            {filteredErrors
              .flatMap((error) =>
                error.occurrences
                  .filter((occ) => {
                    if (spanMs === Infinity) return true;
                    const cutoff = Date.now() - spanMs;
                    return !occ.timestamp || new Date(occ.timestamp).getTime() >= cutoff;
                  })
                  .map((occ, i) => ({
                    occ,
                    error,
                    key: `${error.fingerprint}-${occ.session_id}-${occ.timestamp}-${i}`,
                  })),
              )
              .sort((a, b) => b.occ.timestamp.localeCompare(a.occ.timestamp))
              .map(({ occ, error, key }) => (
                <OccurrenceCard
                  key={key}
                  occurrence={occ}
                  parentError={error}
                  onOpenTranscript={handleOpenTranscript}
                  onOpenHook={handleOpenHook}
                  onIgnore={handleIgnore}
                  onIgnoreTillNow={handleIgnoreTillNow}
                  onSnooze={handleSnooze}
                  onReopen={handleReopen}
                  onCreateTask={handleCreateTask}
                  onGoToSession={handleGoToSession}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
